import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import {
  Button,
  Box,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';

import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { SnackbarSeverityType } from '../../../enums';
import { StyledButton } from '../styled';
import {
  ScoringType,
  GradeScale,
  Subject,
  SubjectComponentRow,
  SubjectSkillRow
} from '../types';
import pallet from '../../../layouts/pallet';
import MarkBookSectionTable from './MarkBookSectionTable';
import { useMarkBookRows, MARK_BOOK_SECTION } from '../hooks/useMarkBookRows';

interface AddSubjectModalProps {
  open: boolean;
  onClose: () => void;
  onAddSubject: (subjectData: {
    subject: Subject;
    scoringType: ScoringType;
    gradeScale?: GradeScale;
    maxMarks?: number;
    components?: SubjectComponentRow[];
    skills?: SubjectSkillRow[];
  }) => void;
  subjectOptions: Subject[];
  gradeScaleOptions: GradeScale[];
  school?: string;
  scoringTypeOptions: ScoringType[];
  isLoadingScoringTypes: boolean;
  isLoadingGradeScales: boolean;
  isLoadingSubjects: boolean;
  existingSubjects: Subject[];
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  markBookEnabled: boolean;
}

const AddSubjectModal: React.FC<AddSubjectModalProps> = ({
  open,
  onClose,
  onAddSubject,
  subjectOptions,
  gradeScaleOptions,
  school,
  scoringTypeOptions,
  isLoadingScoringTypes,
  isLoadingGradeScales,
  isLoadingSubjects,
  existingSubjects,
  showToast,
  markBookEnabled
}) => {
  const [subject, setSubject] = useState('');
  const [scoringType, setScoringType] = useState('');
  const [gradeScale, setGradeScale] = useState('');
  const [maxMarks, setMaxMarks] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const {
    componentRows,
    skillRows,
    rowToDelete,
    addRow,
    updateRow,
    requestDeleteRow,
    confirmDeleteRow,
    cancelDeleteRow,
    resetRows,
    mapRowToComponent,
    mapRowToSkill,
    areAllRowsValid
  } = useMarkBookRows(scoringTypeOptions);

  useEffect(() => {
    if (open) {
      setSubject('');
      setScoringType('');
      setGradeScale('');
      setMaxMarks('');
      setIsAdding(false);
      resetRows();
    }
  }, [open, resetRows]);

  const handleAdd = () => {
    setIsAdding(true);
    const selectedSubject = subjectOptions.find((opt) => opt.id === subject);
    const selectedScoringType = scoringTypeOptions.find(
      (opt) => opt.id === scoringType
    );
    const selectedGradeScale = gradeScaleOptions.find(
      (opt) => opt.id === gradeScale
    );

    if (!selectedSubject || !selectedScoringType) {
      showToast('Please select required fields.', SnackbarSeverityType.error);
      setIsAdding(false);
      return;
    }

    if (markBookEnabled && !areAllRowsValid) {
      showToast(
        'Please fill all required fields in components and skills.',
        SnackbarSeverityType.error
      );
      setIsAdding(false);
      return;
    }

    const numericMax = isEmpty(maxMarks) ? undefined : parseInt(maxMarks, 10);
    onAddSubject({
      subject: selectedSubject,
      scoringType: selectedScoringType,
      ...(selectedGradeScale ? { gradeScale: selectedGradeScale } : {}),
      ...(needsMaxMarks && numericMax !== undefined
        ? { maxMarks: numericMax }
        : {}),
      ...(markBookEnabled
        ? {
            components: componentRows.map(mapRowToComponent),
            skills: skillRows.map(mapRowToSkill)
          }
        : {})
    });
    setSubject('');
    setScoringType('');
    setGradeScale('');
    setMaxMarks('');
    onClose();
    setIsAdding(false);
  };

  const selectedScoringType = scoringTypeOptions.find(
    (st) => st.id === scoringType
  );
  const needsMaxMarks =
    !!scoringType &&
    (!selectedScoringType?.validScores ||
      selectedScoringType.validScores.length === 0);

  useEffect(() => {
    if (!needsMaxMarks) {
      setMaxMarks('');
    }
  }, [needsMaxMarks]);
  const isAddButtonDisabled =
    !subject || !scoringType || (needsMaxMarks && !maxMarks) || isAdding;

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Add Subject in Exam'
        maxWidth='md'
        actions={
          <>
            <Button
              onClick={onClose}
              color='primary'
              sx={{ border: `1px solid ${pallet.primary}` }}
            >
              Cancel
            </Button>
            <StyledButton onClick={handleAdd} disabled={isAddButtonDisabled}>
              {isAdding ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Add'
              )}
            </StyledButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DropdownSelect
            label='Subject'
            placeholder='Select Subject'
            name='subject'
            value={subject}
            options={subjectOptions
              .filter((sub) => !existingSubjects.some((es) => es.id === sub.id))
              .map((sub) => ({
                value: sub.id || '',
                label: sub.name
              }))}
            onChange={(e) => setSubject(e.target.value as string)}
            fullWidth
            loading={isLoadingSubjects}
          />
          <DropdownSelect
            label='Scoring Type'
            placeholder='Select Scoring Type'
            name='scoringType'
            value={scoringType}
            options={scoringTypeOptions.map((st) => ({
              value: st.id || '',
              label: st.name
            }))}
            onChange={(e) => setScoringType(e.target.value as string)}
            required
            fullWidth
            loading={isLoadingScoringTypes}
          />
          {needsMaxMarks && (
            <TextInputWithIcon
              label='Max Marks'
              placeholder='Enter Max Marks'
              name='maxMarks'
              value={maxMarks}
              onChange={(e) => setMaxMarks(e.target.value)}
              fullWidth
              type='number'
              required
            />
          )}
          <DropdownSelect
            label='Grade Scale'
            placeholder='Select Grade Scale'
            name='gradeScale'
            value={gradeScale}
            options={gradeScaleOptions.map((gs) => ({
              value: gs.id || '',
              label: gs.name
            }))}
            onChange={(e) => setGradeScale(e.target.value as string)}
            fullWidth
            loading={isLoadingGradeScales}
          />
          {markBookEnabled && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant='subtitle2' fontWeight='bold'>
                Components
              </Typography>
              <MarkBookSectionTable
                firstFieldLabel='Name'
                rows={componentRows}
                scoringTypeOptions={scoringTypeOptions}
                isLoadingScoringTypes={isLoadingScoringTypes}
                onUpdateRow={(update) =>
                  updateRow(MARK_BOOK_SECTION.COMPONENT, update)
                }
                onAddRow={() => addRow(MARK_BOOK_SECTION.COMPONENT)}
                onRequestDeleteRow={(index) =>
                  requestDeleteRow(MARK_BOOK_SECTION.COMPONENT, index)
                }
              />
              <Divider sx={{ my: 1 }} />
              <Typography variant='subtitle2' fontWeight='bold'>
                Skills
              </Typography>
              <MarkBookSectionTable
                firstFieldLabel='Description'
                rows={skillRows}
                scoringTypeOptions={scoringTypeOptions}
                isLoadingScoringTypes={isLoadingScoringTypes}
                onUpdateRow={(update) =>
                  updateRow(MARK_BOOK_SECTION.SKILL, update)
                }
                onAddRow={() => addRow(MARK_BOOK_SECTION.SKILL)}
                onRequestDeleteRow={(index) =>
                  requestDeleteRow(MARK_BOOK_SECTION.SKILL, index)
                }
              />
            </>
          )}
        </Box>
      </DialogModal>
      <ConfirmationDialog
        open={rowToDelete !== null}
        onClose={cancelDeleteRow}
        message='Are you sure you want to delete this row?'
        onConfirm={confirmDeleteRow}
      />
    </>
  );
};

export default AddSubjectModal;
